import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Building2,
  Mail,
  Phone,
  BadgeCheck,
  Image as ImageIcon,
  ShieldCheck,
  Eye,
  EyeOff,
  CheckCircle2,
  UploadCloud,
  FileSignature,
  Settings,
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import axiosInstance from "../../services/api/axios";
import { useUser } from "../../context/UserContext";
import SigningVendors from "./SigningVendors";

const SectionCard = ({ title, subtitle, icon: Icon, children, footer }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="flex items-start gap-3 px-5 pt-5">
      {Icon && (
        <div className="w-10 h-10 rounded-xl bg-flamingo-50 text-flamingo-600 flex items-center justify-center shrink-0">
          <Icon size={20} />
        </div>
      )}
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
    {footer && <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">{footer}</div>}
  </div>
);

const StatusBadge = ({ ok, okText, pendingText }) => (
  <span
    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${
      ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"
    }`}
  >
    {ok ? <CheckCircle2 size={13} /> : null}
    {ok ? okText : pendingText}
  </span>
);

const DocUpload = ({ label, file, preview, onSelect }) => (
  <div className="rounded-xl border border-slate-200 p-4 flex flex-col gap-3">
    <p className="text-sm font-medium text-slate-700">{label}</p>
    <div className="h-28 rounded-lg bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
      {preview ? (
        <img src={preview} alt={label} className="max-h-full max-w-full object-contain" />
      ) : (
        <ImageIcon size={28} className="text-slate-300" />
      )}
    </div>
    <label className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 text-slate-600 text-sm py-2 cursor-pointer hover:bg-slate-50 transition-colors">
      <UploadCloud size={15} />
      {file ? file.name.slice(0, 18) : "Choose file"}
      <input type="file" accept="image/*" className="hidden" onChange={(e) => onSelect(e.target.files?.[0] || null)} />
    </label>
  </div>
);

const ZumaleSettings = () => {
  const { userInfo, setUserInfo } = useUser();
  // Developers nest the customer under `loginIdentifierDetails.customer`; the ZUMALE_USER admin
  // login returns the customer fields FLAT on loginIdentifierDetails. Handle both shapes.
  const lid = userInfo?.loginIdentifierDetails || {};
  const customer = lid.customer || lid;
  const customerId = customer?.customerId;

  const [docs, setDocs] = useState({ letterhead: null, footer: null, stamp: null });
  const [localPreviews, setLocalPreviews] = useState({ letterhead: "", footer: "", stamp: "" });
  const [serverPreviews, setServerPreviews] = useState({ letterhead: "", footer: "", stamp: "" });
  const [isUploading, setIsUploading] = useState(false);

  const [ssl, setSsl] = useState({ sslPassword: "", commonName: "", countryCode: "IN" });
  const [showPwd, setShowPwd] = useState(false);
  const [isCreatingSSL, setIsCreatingSSL] = useState(false);

  const [isDocsUploaded, setIsDocsUploaded] = useState(!!customer?.isLetterheadFooterStampUploaded);
  const [isSslGenerated, setIsSslGenerated] = useState(!!customer?.isSslCertificateGenerated);

  const fetchServerPreviews = async () => {
    if (!customerId || !isDocsUploaded) return;
    try {
      const resp = await axiosInstance.get(`/customers/letter-head-stamp?customer_id=${customerId}`);
      const arr = Array.isArray(resp) ? resp : [];
      const match = (re) => arr.find((o) => re.test(o.key || ""));
      setServerPreviews({
        letterhead: match(/^header\./i)?.url || "",
        footer: match(/^footer\./i)?.url || "",
        stamp: match(/^stamp_image\./i)?.url || "",
      });
    } catch (e) {
      console.error("Failed to load document previews:", e);
    }
  };

  useEffect(() => {
    fetchServerPreviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, isDocsUploaded]);

  const selectDoc = (type) => (file) => {
    setDocs((prev) => ({ ...prev, [type]: file }));
    setLocalPreviews((prev) => {
      if (prev[type]) URL.revokeObjectURL(prev[type]);
      return { ...prev, [type]: file ? URL.createObjectURL(file) : "" };
    });
  };

  const handleUploadDocs = async () => {
    if (!customerId) return toast.error("No admin customer record found.");
    if (!docs.letterhead || !docs.footer || !docs.stamp) {
      return toast.error("Header, footer and stamp are all required.");
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("letterhead_image", docs.letterhead);
      formData.append("footer_image", docs.footer);
      formData.append("stamp_image", docs.stamp);
      await axiosInstance.post(`/customers/letterhead-footer-stamp/${customerId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIsDocsUploaded(true);
      setUserInfo((prev) => {
        const pl = prev?.loginIdentifierDetails || {};
        const patched = pl.customer
          ? { ...pl, customer: { ...pl.customer, isLetterheadFooterStampUploaded: true } }
          : { ...pl, isLetterheadFooterStampUploaded: true };
        return { ...prev, loginIdentifierDetails: patched };
      });
      toast.success("Header, footer & stamp uploaded — they'll appear on invoices and receipts.");
      setDocs({ letterhead: null, footer: null, stamp: null });
      setLocalPreviews({ letterhead: "", footer: "", stamp: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to upload documents.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateSSL = async () => {
    if (!customerId) return toast.error("No admin customer record found.");
    if (!ssl.sslPassword || !ssl.commonName) return toast.error("Passphrase and common name are required.");
    setIsCreatingSSL(true);
    try {
      await axiosInstance.post(
        "/customers/ssl",
        {
          customerId,
          sslPassword: ssl.sslPassword,
          commonName: ssl.commonName,
          countryCode: ssl.countryCode,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setIsSslGenerated(true);
      setUserInfo((prev) => {
        const pl = prev?.loginIdentifierDetails || {};
        const patched = pl.customer
          ? { ...pl, customer: { ...pl.customer, isSslCertificateGenerated: true } }
          : { ...pl, isSslCertificateGenerated: true };
        return { ...prev, loginIdentifierDetails: patched };
      });
      toast.success("SSL certificate generated. Set the same passphrase as ZUMALE_SSL_PASSPHRASE on the API.");
      setSsl({ sslPassword: "", commonName: "", countryCode: "IN" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate SSL certificate.");
    } finally {
      setIsCreatingSSL(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Settings}
        title="Settings"
        subtitle="Manage the Zumalestate profile, document branding, and signing certificate used on invoices & receipts"
      />

        {/* Profile */}
        <SectionCard title="Profile" subtitle="Your Zumalestate admin account" icon={Building2}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-slate-500 flex items-center gap-1.5"><Building2 size={13} /> Company</dt>
              <dd className="text-slate-900 font-medium mt-0.5">{customer?.companyName || "—"}</dd>
            </div>
            <div>
              <dt className="text-slate-500 flex items-center gap-1.5"><BadgeCheck size={13} /> Owner</dt>
              <dd className="text-slate-900 font-medium mt-0.5">{customer?.ownerName || "—"}</dd>
            </div>
            <div>
              <dt className="text-slate-500 flex items-center gap-1.5"><Mail size={13} /> Email</dt>
              <dd className="text-slate-900 font-medium mt-0.5">{customer?.emailId || "—"}</dd>
            </div>
            <div>
              <dt className="text-slate-500 flex items-center gap-1.5"><Phone size={13} /> Mobile</dt>
              <dd className="text-slate-900 font-medium mt-0.5">{customer?.mobileNoWa || "—"}</dd>
            </div>
          </dl>
        </SectionCard>

        {/* Header / Footer / Stamp */}
        <SectionCard
          title="Document Branding"
          subtitle="Header, footer & stamp applied to generated invoices and receipts (PDF)"
          icon={ImageIcon}
          footer={
            <div className="flex items-center justify-between">
              <StatusBadge ok={isDocsUploaded} okText="Uploaded" pendingText="Not uploaded yet" />
              <button
                type="button"
                onClick={handleUploadDocs}
                disabled={isUploading}
                className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
              >
                <UploadCloud size={16} /> {isUploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <DocUpload label="Header" file={docs.letterhead} preview={localPreviews.letterhead || serverPreviews.letterhead} onSelect={selectDoc("letterhead")} />
            <DocUpload label="Footer" file={docs.footer} preview={localPreviews.footer || serverPreviews.footer} onSelect={selectDoc("footer")} />
            <DocUpload label="Stamp" file={docs.stamp} preview={localPreviews.stamp || serverPreviews.stamp} onSelect={selectDoc("stamp")} />
          </div>
        </SectionCard>

        {/* SSL */}
        <SectionCard
          title="Signing Certificate (SSL)"
          subtitle="Digitally signs invoice & receipt PDFs. Generate once with a passphrase."
          icon={ShieldCheck}
          footer={
            <div className="flex items-center justify-between">
              <StatusBadge ok={isSslGenerated} okText="Generated" pendingText="Not generated yet" />
              <button
                type="button"
                onClick={handleCreateSSL}
                disabled={isCreatingSSL || !ssl.sslPassword || !ssl.commonName}
                className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
              >
                <ShieldCheck size={16} /> {isCreatingSSL ? "Generating…" : "Generate SSL"}
              </button>
            </div>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className="text-sm font-medium text-slate-700">Passphrase</label>
              <div className="mt-1 relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={ssl.sslPassword}
                  onChange={(e) => setSsl((p) => ({ ...p, sslPassword: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 py-2.5 pl-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
                  placeholder="Signing passphrase"
                />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="sm:col-span-1">
              <label className="text-sm font-medium text-slate-700">Common Name</label>
              <input
                type="text"
                value={ssl.commonName}
                onChange={(e) => setSsl((p) => ({ ...p, commonName: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
                placeholder="zumalestate.com"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="text-sm font-medium text-slate-700">Country Code</label>
              <input
                type="text"
                maxLength={2}
                value={ssl.countryCode}
                onChange={(e) => setSsl((p) => ({ ...p, countryCode: e.target.value.toUpperCase() }))}
                className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
                placeholder="IN"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            The passphrase you set here must match the API's <code className="bg-slate-100 px-1 rounded">ZUMALE_SSL_PASSPHRASE</code>{" "}
            so the invoice cron can auto-sign PDFs.
          </p>
        </SectionCard>

        {/* Aadhaar eSign vendors */}
        <SectionCard
          title="Signing Vendors"
          subtitle="Aadhaar eSign providers used to send buyer documents for digital signature (priority-ordered with fallback)."
          icon={FileSignature}
        >
          <SigningVendors embedded />
        </SectionCard>
    </div>
  );
};

export default ZumaleSettings;
